import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth';
import { detectPlatform } from '../lib/detectPlatform';
import type { PlatformId, UAData } from '../lib/detectPlatform';
import { DOWNLOAD_PLATFORMS } from '../lib/downloadPlatforms';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';

// Pick the R2 download channel from the host the page is served on, mirroring
// the installer.yml publish split: staging (stage.tombolomarine.com) + local dev
// preview the continuously-published staging channel, while production serves
// tagged releases. This lets the staging site test downloads without a release
// tag. The `file` values in DOWNLOAD_PLATFORMS are the exact R2 object keys.
const STAGING_BASE = 'https://cdn.tombolomarine.com/neomaris-core/staging/latest';
const PRODUCTION_BASE = 'https://cdn.tombolomarine.com/neomaris-core/latest';

function downloadBase(): string {
  if (typeof window === 'undefined') return PRODUCTION_BASE;
  const host = window.location.hostname;
  const isStaging =
    host === 'stage.tombolomarine.com' || host === 'localhost' || host === '127.0.0.1';
  return isStaging ? STAGING_BASE : PRODUCTION_BASE;
}

const RELEASE_BASE = downloadBase();

const UbuntuIcon = (
  <svg className="icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-7 10a2 2 0 110-4 2 2 0 010 4zm3.7 5.97a2 2 0 11-2-3.46 2 2 0 012 3.46zm0-7.94a2 2 0 11-2-3.46 2 2 0 012 3.46zM12 18a6 6 0 01-5.74-4.27 3 3 0 001.96-1.13H12a6 6 0 01.04 5.4zm-3.78-9.4a3 3 0 00-1.96-1.13A6 6 0 0112 6c.01.27.02 3.69 0 5.4a3 3 0 00-3.78-2.8zM12 16a4 4 0 110-8 4 4 0 010 8z" />
  </svg>
);

// The catalogue itself is pure data in lib/downloadPlatforms.ts, where a test
// can check it without a DOM. Only the icon — the one piece that has to be
// JSX — is bound here.
const PLATFORM_ICONS: Record<PlatformId, ReactNode> = {
  'linux-deb': UbuntuIcon,
  'linux-arm64': UbuntuIcon,
};

// A single line inside a terminal block: an optional command and an optional
// `#` comment. A line with only `comment` renders as a full-line comment; a
// line with both renders the comment inline after the command (the verify
// block). Empty objects render a blank spacer line.
type TermLine = { cmd?: string; comment?: string };

function CodeBlock({ lines, label }: { lines: TermLine[]; label: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Copy only the runnable commands — comments and blank spacers are dropped so
  // pasting into a shell just works.
  const copyText = lines
    .map((line) => line.cmd ?? '')
    .filter((cmd) => cmd.length > 0)
    .join('\n');

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  function handleCopy() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        setCopied(true);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard denied (permissions, insecure context) — leave the button as-is.
      });
  }

  return (
    <div className="terminal">
      <button
        type="button"
        className="terminal-copy"
        onClick={handleCopy}
        aria-label={`Copy ${label} commands`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre>
        <code>
          {lines.map((line, i) => (
            <span className="term-line" key={i}>
              {line.cmd ? <span className="term-cmd">{line.cmd}</span> : null}
              {line.comment ? (
                <span className="term-comment">{`${line.cmd ? '  ' : ''}# ${line.comment}`}</span>
              ) : null}
              {'\n'}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
}

export function Downloads() {
  useDocumentTitle('Downloads - Neomaris');
  // Downloads require a signed-in account. We keep the platform cards (logos,
  // names, recommended-for-you hint) visible to everyone so the page still
  // reads as a real destination, but the per-card action sends logged-out
  // visitors to sign in first — `?from=/downloads` returns them here, where the
  // button becomes the real installer link.
  const { isAuthenticated } = useAuth();
  const [recommended, setRecommended] = useState<PlatformId | null>(null);
  // The version every installer below carries. It comes from the same verified
  // release manifest the boats update against (GET /v1/config →
  // coreLatestVersion), so it always describes the artifacts this host's
  // download channel actually serves — on stage.tombolomarine.com that is the
  // staging channel, which can run ahead of production. Null (manifest URL
  // unset, or nothing verified yet) renders nothing rather than a guess.
  const { config: clientConfig } = useClientConfig();
  const latestVersion = clientConfig?.coreLatestVersion ?? null;

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const uaData = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
    setRecommended(detectPlatform(navigator.userAgent, uaData));
  }, []);

  return (
    <>
      <section className="page-hero">
        <h1 className="reveal">Download Neomaris Core</h1>
        <p className="reveal reveal-delay-1">
          Install on the Linux computer you keep aboard your boat — a Raspberry Pi is the usual
          choice, and any Debian or Ubuntu machine works. Free during early access.
        </p>
        {/* No `reveal` class here on purpose. MarketingLayout's reveal observer
            collects `.reveal` elements once per route change, and this chip
            mounts later — when GET /v1/config resolves — so it is never
            observed and never gets `is-visible`. It carries its own fade-in
            instead of relying on that mechanism. */}
        {latestVersion ? <p className="download-version">Version {latestVersion}</p> : null}
      </section>

      <section className="section section--tight-top downloads-section">
        <div className="section-inner">
          <div className="download-grid reveal">
            {DOWNLOAD_PLATFORMS.map((platform) => {
              const isRecommended = platform.id === recommended;
              const href = `${RELEASE_BASE}/${platform.file}`;
              return (
                <div
                  key={platform.id}
                  className={`download-card${isRecommended ? ' recommended' : ''}`}
                >
                  {isRecommended ? (
                    <span className="recommended-pill">Recommended for your system</span>
                  ) : null}
                  {PLATFORM_ICONS[platform.id]}
                  <h3>{platform.name}</h3>
                  <p>{platform.subtitle}</p>
                  {isAuthenticated ? (
                    <a href={href} className="btn btn-primary" rel="noopener noreferrer" download>
                      Download
                    </a>
                  ) : (
                    <Link to="/login?from=/downloads" className="btn btn-primary">
                      Sign in to download
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
          <p className="download-footnote reveal reveal-delay-1">
            Downloads are served from{' '}
            <a href={RELEASE_BASE} rel="noopener noreferrer">
              cdn.tombolomarine.com
            </a>
            . Verify your download against the published{' '}
            <a href={`${RELEASE_BASE}/SHA256SUMS`} rel="noopener noreferrer">
              SHA-256 checksums
            </a>
            .
          </p>
        </div>
      </section>

      <section className="section downloads-section">
        <div className="section-inner linux-install">
          <div className="section-heading reveal">
            <h2>Installing on Linux</h2>
            <p>
              For a Raspberry Pi or any Debian/Ubuntu-family boat computer (bookworm / glibc 2.36 or
              newer).
            </p>
          </div>

          <CodeBlock
            label="install"
            lines={[
              {
                comment:
                  '1. Download — pick your architecture (arm64 = Raspberry Pi 4/5; amd64 = Intel/AMD)',
              },
              { cmd: `curl -fLO ${RELEASE_BASE}/neomaris-core_arm64.deb` },
              {},
              { comment: '2. Install (pulls in any missing dependencies)' },
              { cmd: 'sudo dpkg -i neomaris-core_arm64.deb || sudo apt-get install -fy' },
            ]}
          />

          <p className="linux-install-note reveal">
            That&apos;s the whole install. The package registers and starts{' '}
            <code>neomaris-core.service</code> (enabled on boot), puts data in{' '}
            <code>/var/lib/neomaris-core/</code>, and serves the boat interface on{' '}
            <strong>port 1880</strong>. On a fresh machine, open{' '}
            <code>http://&lt;boat-ip&gt;:1880/</code> to run first-time setup; on an upgrade,
            everything (data, boat identity, settings) carries over automatically.
          </p>

          <p className="linux-install-subhead reveal">Verify it&apos;s healthy:</p>

          <CodeBlock
            label="verify"
            lines={[
              { cmd: 'systemctl status neomaris-core', comment: 'should be active (running)' },
              { cmd: 'curl localhost:1880/api/health', comment: 'should report "version"' },
            ]}
          />

          <p className="linux-install-note reveal">
            From then on, you never touch a <code>.deb</code> again. Neomaris Core checks the signed
            release manifest daily; when a newer version exists, the <code>/admin</code> status page
            shows an <strong>Update available</strong> banner with an{' '}
            <strong>Install update</strong> button. One click verifies the signature and checksum,
            snapshots the database, installs, and restarts.
          </p>
        </div>
      </section>

      <section className="section downloads-section" id="uninstall">
        <div className="section-inner linux-install">
          <div className="section-heading reveal">
            <h2>Uninstalling</h2>
          </div>

          <CodeBlock
            label="uninstall"
            lines={[
              {
                comment:
                  'Removes the app + service. "sudo apt purge neomaris-core" also erases boat data.',
              },
              { cmd: 'sudo apt remove neomaris-core' },
            ]}
          />

          <p className="linux-install-note reveal">
            Your boat data — settings and history — is kept unless you ask for it to be erased.
          </p>
        </div>
      </section>

      <section className="section downloads-section downloads-section--last">
        <div className="section-inner">
          <div className="tb-card page-cta reveal">
            <div>
              <h2>Need help installing?</h2>
              <p>
                We&apos;re happy to walk you through setup on your specific hardware. Get in touch
                and a real human will reply.
              </p>
            </div>
            <div className="page-cta__actions">
              <Link to="/contact" className="btn btn-primary">
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
